import { AUTH_ENDPOINTS } from '../authEndpoints';
import { buildAuthHeaders } from '../authHeaders';
import mainApiClient from '../clients/mainApiClient';

function compactObject(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  );
}

function buildLoginPayload({
  initData,
  init_data,
  referralId,
  referral_id,
  deviceId,
  device_id,
  theme,
} = {}) {
  return compactObject({
    init_data: initData ?? init_data,
    referral_id: referralId ?? referral_id,
    device_id: deviceId ?? device_id,
    theme,
  });
}

function buildReturnUrlPayload({ returnUrl, return_url, ...payload }) {
  return compactObject({
    ...payload,
    return_url: returnUrl ?? return_url,
  });
}

export const authApi = {
  getMe() {
    return mainApiClient.get(AUTH_ENDPOINTS.session.getMe, {
      auth: true,
    });
  },

  refreshJwt() {
    return mainApiClient.post(AUTH_ENDPOINTS.session.refreshJwt, undefined, {
      retryOnUnauthorized: false,
      _skipAuthRefresh: true,
    });
  },

  createFreshJwt() {
    return mainApiClient.post(AUTH_ENDPOINTS.session.freshJwt, undefined, {
      auth: true,
    });
  },

  revokeAccess() {
    return mainApiClient.delete(AUTH_ENDPOINTS.session.accessRevoke);
  },

  revokeRefresh() {
    return mainApiClient.delete(AUTH_ENDPOINTS.session.refreshRevoke);
  },

  revokeAll() {
    return mainApiClient.delete(AUTH_ENDPOINTS.session.fullRevoke);
  },

  logout() {
    return mainApiClient.delete(AUTH_ENDPOINTS.session.logout, {
      auth: true,
    });
  },

  login({
    rememberMe = true,
    realIp,
    theme = 'light',
    ...payload
  } = {}) {
    const body = buildLoginPayload({ ...payload, theme });

    return mainApiClient.post(
      AUTH_ENDPOINTS.session.login,
      body,
      {
        headers: buildAuthHeaders({ rememberMe, realIp }),
        retryOnUnauthorized: false,
      }
    );
  },

  validatePassword({
    password,
    token,
    rememberMe = true,
    realIp,
  } = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.session.validatePassword,
      compactObject({ password, token }),
      {
        headers: buildAuthHeaders({ rememberMe, realIp }),
        retryOnUnauthorized: false,
      }
    );
  },

  biometricLogin(payload = {}) {
    const { rememberMe = true, realIp, ...body } = payload;

    return mainApiClient.post(
      AUTH_ENDPOINTS.session.validateBiometry,
      compactObject(body),
      {
        headers: buildAuthHeaders({ rememberMe, realIp }),
        retryOnUnauthorized: false,
      }
    );
  },

  addBiometry(payload = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.biometry.addNew,
      compactObject(payload),
      {
        auth: true,
      }
    );
  },

  removeBiometry() {
    return mainApiClient.post(
      AUTH_ENDPOINTS.biometry.removeBiometry,
      undefined,
      {
        auth: true,
      }
    );
  },

  getRouteMap() {
    return mainApiClient.get(AUTH_ENDPOINTS.permissions.routeMap, {
      auth: true,
    });
  },

  editPersonalData({ setData, set_data, unsetData, unset_data } = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.register.editPersonalData,
      compactObject({
        set_data: setData ?? set_data,
        unset_data: unsetData ?? unset_data,
      }),
      {
        auth: true,
      }
    );
  },

  updateSignedDocuments({ signedDocuments, signed_documents } = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.register.updateSignedDocuments,
      {
        signed_documents: signedDocuments ?? signed_documents ?? [],
      },
      {
        auth: true,
      }
    );
  },

  requestEmailChange({ email } = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.changeEmail.main,
      compactObject({ email }),
      {
        auth: true,
      }
    );
  },

  confirmEmailChange({ code } = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.changeEmail.checkCode,
      compactObject({ code }),
      {
        auth: true,
      }
    );
  },

  requestPhoneCode(payload = {}) {
    const { phone, preferredChannel, preferred_channel } = payload;
    const hasPreferredChannel = Object.prototype.hasOwnProperty.call(payload, 'preferredChannel')
      || Object.prototype.hasOwnProperty.call(payload, 'preferred_channel');
    const nextPreferredChannel = Object.prototype.hasOwnProperty.call(payload, 'preferredChannel')
      ? preferredChannel
      : preferred_channel;

    return mainApiClient.post(
      AUTH_ENDPOINTS.changePhone.requestCode,
      compactObject({
        phone,
        ...(hasPreferredChannel ? { preferred_channel: nextPreferredChannel } : {}),
      }),
      {
        auth: true,
      }
    );
  },

  validatePhoneCode({ code } = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.changePhone.validateCode,
      compactObject({ code }),
      {
        auth: true,
      }
    );
  },

  validatePasswordResetCodeNotLoggedIn(payload = {}) {
    const { rememberMe = true, realIp, ...body } = payload;

    return mainApiClient.post(
      AUTH_ENDPOINTS.forgotPassword.validateCodeNotLoggedIn,
      buildReturnUrlPayload(body),
      {
        headers: buildAuthHeaders({ rememberMe, realIp }),
        retryOnUnauthorized: false,
      }
    );
  },

  validatePasswordResetCode({ code } = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.forgotPassword.validateCode,
      compactObject({ code }),
      {
        auth: true,
      }
    );
  },

  recoverPasswordNotLoggedIn(payload = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.forgotPassword.mainNotLoggedIn,
      buildReturnUrlPayload(payload),
      {
        retryOnUnauthorized: false,
      }
    );
  },

  recoverPassword({ channel } = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.forgotPassword.main,
      compactObject({ channel }),
      {
        auth: true,
      }
    );
  },

  checkPassword({ currentPassword, current_password } = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.password.check,
      compactObject({
        current_password: currentPassword ?? current_password,
      }),
      {
        auth: true,
      }
    );
  },

  updatePassword({ currentPassword, current_password, newPassword, new_password } = {}) {
    return mainApiClient.patch(
      AUTH_ENDPOINTS.password.update,
      compactObject({
        current_password: currentPassword ?? current_password,
        new_password: newPassword ?? new_password,
      }),
      {
        auth: true,
      }
    );
  },

  setInitialPassword({ password } = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.password.setInitial,
      compactObject({ password }),
      {
        auth: true,
      }
    );
  },

  getNotifications({ limit, lastId, last_id } = {}) {
    return mainApiClient.get(AUTH_ENDPOINTS.notifications.getAll, {
      auth: true,
      params: {
        limit,
        last_id: lastId ?? last_id,
      },
    });
  },

  updateNotifications({ mode, all, startId, start_id, endId, end_id } = {}) {
    return mainApiClient.post(
      AUTH_ENDPOINTS.notifications.update,
      compactObject({
        mode,
        all,
        start_id: startId ?? start_id,
        end_id: endId ?? end_id,
      }),
      {
        auth: true,
      }
    );
  },

  sendReferralMessage() {
    return mainApiClient.post(AUTH_ENDPOINTS.referrals.sendMessage, undefined, {
      auth: true,
    });
  },
};
